import { LockOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import AuthContainer from 'components/containers/AuthContainer';
import { Button } from 'components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from 'services/RouteService';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { toast } from 'sonner';

type FormValues = { new_password: string; re_new_password: string };

export default function PasswordResetPage() {
  // Hooks
  const { t } = useTranslation();
  const urlParams = useParams();
  const navigate = useNavigate();
  const form = useForm<FormValues>({ mode: 'onChange', defaultValues: { new_password: '', re_new_password: '' } });

  const { loading, run, error } = useRequest(params => ApiUsersManager.resetPassword(params), {
    manual: true,
    onSuccess: () => {
      toast.success(t('passwordReset.success.reset'));

      setTimeout(() => navigate(routes.login.route), 1000);
    },
    onError: () => {
      toast.error(t('passwordReset.errors.reset'));
    },
  });

  const onFinish = (values: FormValues) => {
    run({
      uid: urlParams.uid,
      token: urlParams.token,
      new_password: values.new_password,
      re_new_password: values.re_new_password,
    });
  };

  const serverPasswordErrors: string[] | undefined = (error as any)?.response?.data?.new_password;

  return (
    <AuthContainer
      title={t('passwordReset.title')}
      description={t('passwordReset.description.reset')}
      mode="passwordReset"
      bottomLinkTo="login"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onFinish)} className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="new_password"
            rules={{
              required: t('passwordReset.fields.password.errors.required'),
              minLength: { value: 8, message: t('passwordReset.fields.password.errors.minLength') },
              deps: ['re_new_password'],
            }}
            render={({ field }) => (
              <FormItem>
                <div className="relative">
                  <LockOutlined className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-muted-foreground" />
                  <FormControl>
                    <Input
                      {...field}
                      type="password"
                      placeholder={t('passwordReset.fields.password.placeholder')}
                      className="pl-9"
                      autoCapitalize="none"
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="re_new_password"
            rules={{
              required: t('passwordReset.fields.passwordConfirmation.errors.required'),
              minLength: { value: 8, message: t('passwordReset.fields.passwordConfirmation.errors.minLength') },
              validate: value =>
                !value ||
                form.getValues('new_password') === value ||
                t('passwordReset.fields.passwordConfirmation.errors.passwordsNotMatch'),
            }}
            render={({ field }) => (
              <FormItem>
                <div className="relative">
                  <LockOutlined className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-muted-foreground" />
                  <FormControl>
                    <Input
                      {...field}
                      type="password"
                      placeholder={t('passwordReset.fields.passwordConfirmation.placeholder')}
                      className="pl-9"
                      autoCapitalize="none"
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {Array.isArray(serverPasswordErrors) && serverPasswordErrors.length > 0 && (
            <div className="mb-6">
              {serverPasswordErrors.map((errorMessage: string, index: number) => (
                <span key={index} className="text-destructive-text">
                  {errorMessage}
                </span>
              ))}
            </div>
          )}

          <Button type="submit" size="lg" className="w-full" disabled={loading} loading={loading}>
            {t('passwordReset.actions.reset')}
          </Button>
        </form>
      </Form>
    </AuthContainer>
  );
}
